import { useQuery } from "@tanstack/react-query";
import type { AdminInbox } from "@mm/shared";
import type { ReactNode } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router";
import { api, signOut } from "@/lib/api";
import { Icon } from "./icons";
import { cx } from "./kit";
import { Logo } from "./Logo";

const PRIMARY = [
  { to: "/", label: "Inbox", icon: "inbox", end: true },
  { to: "/events", label: "Events", icon: "calendar" },
  { to: "/members", label: "People", icon: "users" },
  { to: "/ads", label: "Ads", icon: "megaphone" },
];
export const SECONDARY = [
  { to: "/coop", label: "Co-op", icon: "heart" },
  { to: "/places", label: "Map places", icon: "map" },
  { to: "/categories", label: "Map categories", icon: "tag" },
  { to: "/cohorts", label: "Cohorts & codes", icon: "key" },
  { to: "/system", label: "System health", icon: "pulse" },
];

function useInboxCount() {
  const q = useQuery({ queryKey: ["inbox"], queryFn: () => api<AdminInbox>("/v1/admin/inbox"), refetchInterval: 60_000 });
  const c = q.data?.counts;
  return c ? c.eventSuggestions + c.placeSuggestions + c.unansweredCare + c.reports + c.deadLetters : 0;
}

/** Phone: bottom tab bar. Laptop (lg+): left sidebar. Same routes either way. */
export function Shell() {
  const inbox = useInboxCount();
  const nav = useNavigate();
  return (
    <div className="min-h-dvh lg:flex">
      <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r border-line bg-white p-4 lg:flex">
        <Link to="/" className="mb-6 block px-2" aria-label="Modern Motherhood Chicago: inbox">
          <Logo className="h-12 w-auto" />
        </Link>
        {[...PRIMARY, ...SECONDARY].map((n) => (
          <NavLink key={n.to} to={n.to} end={"end" in n} className={({ isActive }) => cx("mb-1 flex min-h-11 items-center gap-3 rounded-xl px-3 font-medium", isActive ? "bg-ink text-white" : "hover:bg-mist")}>
            <Icon name={n.icon} className="size-5" /> {n.label}
            {n.to === "/" && inbox ? <span className="ml-auto rounded-full bg-rose px-2 text-xs font-bold text-ink">{inbox}</span> : null}
          </NavLink>
        ))}
        <button onClick={() => void signOut().then(() => nav("/login"))} className="mt-auto flex min-h-11 items-center gap-3 rounded-xl px-3 text-muted hover:bg-mist">
          <Icon name="logout" className="size-5" /> Sign out
        </button>
      </aside>

      <main className="mx-auto w-full min-w-0 max-w-3xl px-4 pt-safe pb-28 lg:pb-10">
        <Outlet />
      </main>

      <nav className="pb-safe fixed inset-x-0 bottom-0 z-20 flex border-t border-line bg-cream/95 backdrop-blur lg:hidden" aria-label="Main">
        {[...PRIMARY, { to: "/more", label: "More", icon: "more" }].map((n) => (
          <NavLink key={n.to} to={n.to} end={"end" in n} className={({ isActive }) => cx("relative flex min-h-16 flex-1 flex-col items-center justify-center gap-0.5 text-xs", isActive ? "font-bold text-ink" : "text-muted")}>
            {({ isActive }) => (
              <>
                {isActive ? <span className="absolute top-0 h-[3px] w-6 rounded-full bg-rose" /> : null}
                <Icon name={n.icon} className="size-6" />
                {n.label}
                {n.to === "/" && inbox ? <span className="absolute top-2 left-[56%] min-w-5 rounded-full bg-ink px-1.5 text-[10px] font-bold text-white">{inbox}</span> : null}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function PageHeader({ title, back, action, subtitle }: { title: string; back?: string | true; action?: ReactNode; subtitle?: ReactNode }) {
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-10 -mx-4 mb-4 flex items-center gap-2 bg-cream/95 px-4 py-3 backdrop-blur pt-safe">
      {back ? (
        <button onClick={() => (back === true ? nav(-1) : nav(back))} className="-ml-2 grid size-11 place-items-center rounded-full hover:bg-mist" aria-label="Back">
          <Icon name="back" />
        </button>
      ) : null}
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-2xl font-bold tracking-tight">{title}</h1>
        {subtitle ? <p className="truncate text-sm text-muted">{subtitle}</p> : null}
      </div>
      {action}
    </header>
  );
}
